/**
 * Reports > Trailing 12 Months (/reports/trailing-12).
 *
 * A store-by-month grid of sales for the last twelve completed calendar months, with a
 * total column and a total row and an Excel download. Replaces the QuickSight dashboard
 * "Trailing 12 Monthly Sales by Store".
 *
 * Thin page: the screen is the shared `Trailing12` component in `@ampersandbrands/ui`
 * (presentational); this file fetches through `useTrailing12` and adds the page chrome.
 *
 * RBAC: head office only, `reports.trailing12`. No store-manager view.
 *
 * Resilience: wrapped in an ErrorBoundary and degrades to a quiet "not available yet"
 * card on 404/503 while the api ships.
 */

import { useState } from 'react';
import { Trailing12 as Trailing12Screen } from '@ampersandbrands/ui';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { isNotAvailable } from '@/lib/useStores';
import { downloadFinanceXlsx, useTrailing12 } from '@/lib/useFinance';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { AccessDeniedCard, ErrorCard, NotAvailableCard, StoresHeader } from '../stores/storesShared';

/** The accessSlug that grants this report. Head office only. */
const SLUG = 'reports.trailing12';

function Trailing12Inner() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, SLUG, user?.superAdmin);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const dataQ = useTrailing12(canView);

  async function handleExport() {
    setExporting(true);
    setToast(null);
    try {
      await downloadFinanceXlsx('trailing-12/export', '', 'trailing-12-months.xlsx');
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setExporting(false);
    }
  }

  if (user && !canView) return <AccessDeniedCard what="Trailing 12 Months" />;

  const header = (
    <StoresHeader
      eyebrow="Reports"
      title="Trailing 12 Months"
      subtitle="Sales by store for each of the last twelve completed calendar months, with a total for every store and every month. Refreshed from CounterPoint once a day."
      actions={<ToolPinToggle toolSlug="trailing-12" toolName="Trailing 12 Months" />}
    />
  );

  if (isNotAvailable(dataQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="Trailing 12 Months" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      {toast && (
        <p role="alert" className="mb-3 text-sm text-red-700 dark:text-red-300">
          {toast}
        </p>
      )}
      <Trailing12Screen
        months={dataQ.months}
        rows={dataQ.rows}
        totals={dataQ.totals}
        asOf={dataQ.asOf}
        loading={dataQ.isLoading}
        error={dataQ.error ? <ErrorCard message={dataQ.error.message} /> : undefined}
        onExport={() => void handleExport()}
        exporting={exporting}
      />
    </div>
  );
}

export function Trailing12() {
  return (
    <ErrorBoundary>
      <Trailing12Inner />
    </ErrorBoundary>
  );
}
